import { FileText, Layers, ShieldAlert } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import RevisionReassurance from "./RevisionReassurance";

/**
 * Iter-128 — Trust pass.
 *
 * Before a user uploads a script revision we force an explicit choice
 * between:
 *
 *   - "full_script"   → a COMPLETE new version of the script. Scenes
 *                       absent from it will be flagged as removed.
 *   - "revised_pages" → coloured / replacement pages covering ONLY a
 *                       subset of scenes. Scenes not in the pages are
 *                       untouched.
 *
 * This distinction is the single most important safety lever in the
 * revision flow — without it the app used to treat revised pages as a
 * full replacement and mass-omit every scene that wasn't in the
 * partial PDF.
 */
export default function ScriptRevisionKindPicker({
    open,
    onOpenChange,
    onPick,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="script-revision-kind-picker"
                className="max-w-md bg-card border-white/10"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white">
                        What are you uploading?
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Tell us what kind of script change this is — that way
                        we only compare the right scenes.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-2 space-y-2">
                    <button
                        type="button"
                        data-testid="revision-kind-revised-pages"
                        onClick={() => onPick("revised_pages")}
                        className="w-full text-left rounded-xl border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 p-4 transition-colors"
                    >
                        <div className="flex items-center gap-2 text-emerald-200 font-semibold">
                            <Layers size={16} />
                            A few changed pages
                        </div>
                        <p className="mt-1.5 text-[12px] text-neutral-300 leading-relaxed">
                            Coloured pages (blue, pink, yellow…) or a PDF
                            covering only some scenes. We&apos;ll only review
                            the scenes you upload — everything else stays
                            exactly as it is.
                        </p>
                        <p className="mt-1.5 text-[11px] text-emerald-300/80">
                            Safer · recommended for ongoing production
                        </p>
                    </button>

                    <button
                        type="button"
                        data-testid="revision-kind-full-script"
                        onClick={() => onPick("full_script")}
                        className="w-full text-left rounded-xl border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 p-4 transition-colors"
                    >
                        <div className="flex items-center gap-2 text-amber-200 font-semibold">
                            <FileText size={16} />
                            An updated full script
                        </div>
                        <p className="mt-1.5 text-[12px] text-neutral-300 leading-relaxed">
                            A complete, end-to-end new version of the
                            script. Scenes missing from it will be flagged
                            as omitted — only after you explicitly approve.
                            Nothing is ever deleted.
                        </p>
                        <p className="mt-1.5 text-[11px] text-amber-300/80 flex items-center gap-1">
                            <ShieldAlert size={11} />
                            Structural — review carefully before approving
                        </p>
                    </button>
                </div>

                <RevisionReassurance
                    variant="compact"
                    testid="revision-kind-picker-reassurance"
                    className="mt-3"
                />

                <button
                    type="button"
                    data-testid="revision-kind-cancel"
                    onClick={() => onOpenChange(false)}
                    className="mt-3 text-xs text-neutral-400 hover:text-neutral-200 underline underline-offset-4 self-center mx-auto"
                >
                    Cancel
                </button>
            </DialogContent>
        </Dialog>
    );
}
